import ClearIcon from '@mui/icons-material/Clear';
import LoadingButton from '@mui/lab/LoadingButton';
import { IconButton, Stack, Theme, useTheme } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import TextField from '@mui/material/TextField';
import React, { useState } from 'react';
import StyledCardTitleBar from './StyledCardTitleBar';

/**
 * TextInputDialog - A reusable Material UI dialog component for text input
 *
 * @param {Object} props
 * @param {boolean} props.open - Controls whether the dialog is open
 * @param {function} props.onClose - Callback when dialog is closed without submission
 * @param {function} props.onSubmit - Callback when form is submitted with text value
 * @param {string} props.title - Dialog title text
 * @param {string} props.contentText - Prompt or description text
 * @param {string} props.label - Input field label
 * @param {string} props.placeholder - Input field placeholder
 * @param {string} props.initialValue - Initial value for the text field
 * @param {string} props.cancelButtonText - Text for cancel button
 * @param {string} props.submitButtonText - Text for submit button
 */
const TextInputDialog = ({
    open,
    onClose,
    onSubmit,
    title = 'Enter Information',
    contentText,
    label = 'Input',
    placeholder = '',
    initialValue = '',
    clearButton = false,
    cancelButtonText = 'Cancel',
    submitButtonText = 'Submit',
    clearButtonText = 'Clear',
    allowEmpty = false,
    isLoading = false,
    children,
    maxWidth = 'sm',
    onValueChange,
    showErrorOnEmpty = false,
}: {
    open: boolean;
    onClose: () => void;
    onSubmit: (value: string) => void;
    title?: string;
    contentText?: string | null;
    label?: string;
    placeholder?: string;
    initialValue?: string;
    clearButton?: boolean;
    cancelButtonText?: string;
    submitButtonText?: string;
    clearButtonText?: string;
    allowEmpty?: boolean;
    isLoading?: boolean;
    children?: React.ReactNode;
    maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    onValueChange?: (value: string) => void;
    showErrorOnEmpty?: boolean;
}) => {
    const theme: Theme = useTheme();
    const [value, setValue] = useState(initialValue);

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        onSubmit(value);
        setValue(initialValue); // Reset the field
    };

    const handleClose = () => {
        setValue(initialValue); // Reset the field
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth={maxWidth} fullWidth>
            <StyledCardTitleBar title={title} />
            <form onSubmit={handleSubmit}>
                <DialogContent>
                    {contentText !== null ? <DialogContentText gutterBottom>{contentText}</DialogContentText> : null}
                    <TextField
                        autoFocus
                        margin="dense"
                        id="input-text"
                        label={label}
                        placeholder={placeholder}
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={value}
                        disabled={isLoading}
                        error={showErrorOnEmpty && value.trim() === ''}
                        helperText={showErrorOnEmpty && value.trim() === '' ? '* Required' : undefined}
                        onChange={e => { setValue(e.target.value); onValueChange?.(e.target.value); }}
                        InputProps={{
                            endAdornment: clearButton && value && (
                                <IconButton
                                    color="error"
                                    onClick={() => {
                                        setValue('');
                                    }}
                                >
                                    <ClearIcon />
                                </IconButton>
                            ),
                        }}
                    />
                    {children}
                </DialogContent>
                <DialogActions
                    sx={{
                        backgroundColor: theme.palette.grey[100],
                        borderTop: `1px solid ${theme.palette.divider}`,
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                    }}
                >
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
                        {clearButton && value && (
                            <Button
                                color="error"
                                onClick={() => {
                                    onSubmit('');
                                    setValue('');
                                }}
                                disabled={isLoading}
                            >
                                {clearButtonText}
                            </Button>
                        )}
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                        <Button color="secondary" onClick={handleClose} disabled={isLoading}>
                            {cancelButtonText}
                        </Button>
                        <LoadingButton
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={!allowEmpty && value.trim() === ''}
                            loading={isLoading}
                        >
                            <span>{submitButtonText}</span>
                        </LoadingButton>
                    </Stack>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default TextInputDialog;
